import { checkinSymptomFieldKeys } from "./symptom-entry";

// TES-1561 (Ö-866) — which monthly check-in (F-05, template `milestone`)
// answers the COACH may see in her own case for that check-in. Emelie
// 2026-10-22: "coachen ska bara se och svara på den coachrelevanta delen,
// exempelvis mående, vardag, motivation, hinder, önskat stöd och patientens
// coachrelaterade fritext" and "Coachen ska inte se eller förväntas kommentera
// medicinska originalsvar, läkarflaggor, dos, recept eller behandlingsbeslut."
//
// F-05 is "Månadens medicinska avstämning": the form is written for the
// doctor's monthly assessment, so most of it is MEDICAL. Derived from the
// published seeds v1–v11 (forms/templates/milestone-content.v*.ts):
//
//   week_wellbeing ("Hur har du mått?", v5+)       → COACH ("mående")
//   month_daily_function ("Hur fungerar vardagen med behandlingen?")
//                                                  → COACH ("vardag")
//   month_weight (+ month_weight_measured_on)      → COACH, as F-04's
//       week_weight: the coach already follows her weight every week
//   v1: feelingOverall, currentWeightKg            → COACH (same meaning)
//
// Everything else is MEDICAL: dose and adherence ("Aktuell behandling och
// följsamhet"), the treatment effect and appetite signals, waist, blood
// pressure and pulse, every symptom and safety question, health changes
// (medicines, diagnosis, pregnancy, procedures), the change in drive and
// mood, what she wants to discuss with the doctor and her question to him.
//
// F-05 has no question about motivation, obstacles or wished-for support,
// and no free text addressed to the coach, so those parts of Emelie's list
// have nothing to map to. The borderline fields (the weight's free-text
// context, hunger, satiety, cravings) are MEDICAL until she says otherwise:
// failing closed keeps a medical answer away from the coach.
//
// An unknown key has no class and is NOT shown, for the same reason.

export const F05_FIELD_CLASSES = ["COACH", "MEDICAL"] as const;
export type F05FieldClass = (typeof F05_FIELD_CLASSES)[number];

export const F05_FIELD_CLASS: Readonly<Record<string, F05FieldClass>> = {
  // COACH
  week_wellbeing: "COACH",
  month_daily_function: "COACH",
  month_weight: "COACH",
  month_weight_measured_on: "COACH",
  feelingOverall: "COACH",
  currentWeightKg: "COACH",
  // MEDICAL — "Aktuell behandling och följsamhet"
  monthIndex: "MEDICAL",
  month_medicine_confirm: "MEDICAL",
  month_last_dose_date: "MEDICAL",
  month_adherence: "MEDICAL",
  month_adherence_reason: "MEDICAL",
  // MEDICAL — "Vikt, effekt och aptitsignaler", the part that is not weight
  month_weight_context: "MEDICAL",
  month_hunger: "MEDICAL",
  month_satiety: "MEDICAL",
  month_cravings: "MEDICAL",
  month_effect_change: "MEDICAL",
  month_waist: "MEDICAL",
  month_bp_systolic: "MEDICAL",
  month_bp_diastolic: "MEDICAL",
  month_bp_measured_on: "MEDICAL",
  month_resting_pulse: "MEDICAL",
  // MEDICAL — "Besvär och säkerhet"
  month_symptoms: "MEDICAL",
  month_symptom_types: "MEDICAL",
  month_symptom_other_text: "MEDICAL",
  month_symptom_change: "MEDICAL",
  month_symptom_care: "MEDICAL",
  month_intake: "MEDICAL",
  month_urgent_signals: "MEDICAL",
  // v2–v6 per-symptom severity
  ...Object.fromEntries(
    [
      "nausea",
      "vomiting",
      "constipation",
      "diarrhea",
      "abdominal_pain",
      "reflux",
      "dizziness",
      "fatigue",
      "headache",
      "injection_site",
      "other",
    ].map((code) => [`month_symptom_severity_${code}`, "MEDICAL" as const]),
  ),
  // v7+ per-symptom questions
  ...Object.fromEntries(
    checkinSymptomFieldKeys("month").map((key) => [key, "MEDICAL" as const]),
  ),
  // MEDICAL — "Förändringar i hälsan"
  month_new_medicines: "MEDICAL",
  month_new_medicines_list: "MEDICAL",
  month_new_diagnosis: "MEDICAL",
  month_new_diagnosis_text: "MEDICAL",
  month_pregnancy_change: "MEDICAL",
  month_procedure: "MEDICAL",
  month_other_change: "MEDICAL",
  // MEDICAL — "Din upplevelse och dina frågor", the doctor's part
  month_drive_mood_change: "MEDICAL",
  month_treatment_preference: "MEDICAL",
  month_question: "MEDICAL",
  // v15 (TES-1637) — the text under "Annat", in place of month_question
  month_other_question: "MEDICAL",
  // v16 (TES-1638) — the dose wish, asked before month_treatment_preference
  month_dose_wish: "MEDICAL",
  month_confirm: "MEDICAL",
  // v1
  sideEffectsOpen: "MEDICAL",
};

/** The field's class, or null for a key nobody has classified. */
export function f05FieldClass(key: string): F05FieldClass | null {
  return F05_FIELD_CLASS[key] ?? null;
}

/** COACH answers reach the coach's case; MEDICAL and unknown never do. */
export function isCoachVisibleF05Field(key: string): boolean {
  return f05FieldClass(key) === "COACH";
}
